import { type Checkpoint } from '../../../shared/sync/schemas';
import { pullProcesses, pushProcesses } from '../../sync/processes';
import { type ProcessDoc } from '../collections/processes';
import { type ClewDatabase } from '../database';
import {
  checkpointFallbackId,
  withCheckpointFallback,
} from './checkpointFallback';
import { lwwConflictHandler } from './conflictHandler';
import {
  replicateRxCollection,
  type RxReplicationState,
} from 'rxdb/plugins/replication';

const REPLICATION_ID = 'processes-trpc';

export const startProcessesReplication = (
  db: ClewDatabase,
  userId: string,
): RxReplicationState<ProcessDoc, Checkpoint> => {
  const replication = replicateRxCollection<ProcessDoc, Checkpoint>({
    autoStart: true,
    collection: db.processes,
    deletedField: '_deleted',
    live: true,
    pull: {
      batchSize: 100,
      handler: withCheckpointFallback(
        checkpointFallbackId(userId, REPLICATION_ID),
        async (checkpoint, batchSize) => {
          const result = await pullProcesses({
            batchSize,
            checkpoint: checkpoint ?? null,
          });
          if (result.summaries.length > 0) {
            await db.processItemSummaries.bulkUpsert(result.summaries);
          }

          return {
            checkpoint: result.checkpoint ?? undefined,
            documents: result.documents.map((doc) => ({
              ...doc,
              _deleted: Boolean(doc._deleted),
            })),
          };
        },
      ),
    },
    push: {
      batchSize: 50,
      handler: async (rows) => {
        const result = await pushProcesses(
          rows.map((row) => ({
            assumedMasterState: row.assumedMasterState,
            newDocumentState: row.newDocumentState,
          })),
        );
        return result.conflicts.map((conflict) => ({
          ...conflict,
          _deleted: Boolean(conflict._deleted),
        }));
      },
    },
    replicationIdentifier: REPLICATION_ID,
    retryTime: 5_000,
    waitForLeadership: false,
  });

  db.processes.conflictHandler = lwwConflictHandler as never;

  return replication;
};
